<template>
    <div class="rounded border bg-base-100 border-base-300 shadow-lg p-2 flex flex-col h-full">
        <span class="text-lg pt-2 pb-4">Transaksjonsliste</span>
        <div class="flex flex-col h-full">
            <ag-grid-vue
                :grid-options="gridOptions"
                row-model-type="infinite"
                :datasource="tlDatasource"
                :column-defs="colDefs"
                :theme="agTheme"
                class="h-full grow"
            />
        </div>
    </div>
</template>

<script setup lang="ts">

import {computed, inject, PropType, Ref, shallowRef} from 'vue'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {ColDef, GridApi, GridOptions, GridReadyEvent, IDatasource, IGetRowsParams, RowClassParams, SortModelItem} from 'ag-grid-community'
import {getTransactionLinesByIGetRowsParams} from '@/transaction/transaction.service'
import {TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import GenLegTransNameCell from '@/genleg/GenLegTransNameCell.vue'
import GenLegFlatDescriptionCell from '@/genleg/GenLegFlatDescriptionCell.vue'
import GenLegSubAccountCell from '@/genleg/GenLegSubAccountCell.vue'
import GenLegInvoiceCell from '@/genleg/GenLegInvoiceCell.vue'
import GenLegVatCell from '@/genleg/GenLegVatCell.vue'
import {globalDefaultColDef} from '@/agUtils'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'

const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<string> | undefined = inject('panelPathSegment')

const props = defineProps({
    ledgerAccounts: {
        type: Array<LedgerAccount>,
        required: true
    },
    searchParams: {
        type: Object as PropType<TransactionSearchParams>,
        required: true
    }
})

const gridApi = shallowRef<GridApi>()

const tlDatasource = computed<IDatasource>(() => {
    let totalRowCount = 0

    props.ledgerAccounts.forEach((ledger) => {
        totalRowCount += ledger.numTransactionLines
    })

    return {
        rowCount: totalRowCount,
        async getRows(params: IGetRowsParams) {
            const filterModel: Record<string, any> = {
                date: {
                    filterType: 'join',
                    type: 'and',
                    conditions: [
                        {
                            filterType: 'date',
                            type: 'lessThanOrEqual',
                            dateFrom: props.searchParams.toDate
                        },
                        {
                            filterType: 'date',
                            type: 'greaterThanOrEqual',
                            dateFrom: props.searchParams.fromDate
                        }
                    ]
                },
                ...params.filterModel
            }

            if (props.searchParams.project) {
                filterModel.project = {
                    filterType: 'text',
                    type: 'equals',
                    filter: props.searchParams.project
                }
            }

            if (props.searchParams.department) {
                filterModel.department = {
                    filterType: 'text',
                    type: 'equals',
                    filter: props.searchParams.department
                }
            }

            if (props.searchParams.isDeleted) {
                filterModel.deleted = {
                    filterType: 'boolean',
                    type: 'equals',
                    filter: props.searchParams.isDeleted
                }
            }
 
            const sortModel: SortModelItem[] = params.sortModel?.length ? [{
                sort: params.sortModel[0]?.sort || 'asc',
                colId: mapSortableColNames(params.sortModel[0]?.colId) || params.sortModel[0]?.colId
            }] : []

        const tls = await getTransactionLinesByIGetRowsParams({
            ...params,
            search: props.searchParams.query,
            filterModel,
            ...params.filterModel,
            sortModel
        })

            params.successCallback(tls, totalRowCount)
        }
    }
})

const mapSortableColNames = (name: string) => {
    switch(name){
        case 'subAccountName':
            return 'subAccountName'
        case 'invoiceNo':
            return 'invoiceUuid'
        case 'vatOptionId':
            return 'vatOptionId'
        case 'transactionName':
            return 'transactionId'
    }
}

const gridOptions: GridOptions = {
    getRowId: ({data}: { data: TransactionLine }) => data.uuid,
    onGridReady(event: GridReadyEvent) {
        gridApi.value = event.api
    },
    enableCellTextSelection: true,
    suppressContextMenu: true,
    getRowClass: (params: RowClassParams) => {
        if (['incoming-balance', 'outgoing-balance', 'sum-changes'].includes(params.data?.type)) {
            return '!bg-base-200'
        } else {
            return '!bg-base-100'
        }
    },
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: true
    },
    context: {
        panelId,
        panelPathSegment
    }
}

const colDefs: ColDef[] = [
    {
        headerName: 'Dato',
        width: 100,
        field: 'date',
        cellRenderer: ({value}: { value: string }) => formattedDate(value),
        sortable: true,
        sort: 'asc'
    },
    {
        headerName: 'KontoNr',
        width: 75,
        field: 'accountNo',
        cellRenderer: ({value}: { value: string }) => (value),
    },
    {
        headerName: 'Konto',
        width: 160,
        field: 'accountNo',
        valueGetter: (params) => {
            const account = bookkeepingAccounts.value.find(acc => acc.accountNo === params.data?.accountNo)
            return account?.name || ''
        }
    },
    {
        headerName: 'Kunde / leverandør',
        flex: 1,
        field: 'subAccountName',
        cellRenderer: GenLegSubAccountCell
    },
    {
        headerName: 'Beskrivelse',
        flex: 2,
        field: 'description',
        cellRenderer: GenLegFlatDescriptionCell
    },
    {
        headerName: 'Prosjekt',
        width: 100,
        field: 'project',
        cellRenderer: ({value}: { value: string }) => (value),
    },
    {
        headerName: 'Avdeling',
        width: 100,
        field: 'department',
        cellRenderer: ({value}: { value: string }) => (value),
    },
    {
        headerName: 'Bilag',
        width: 100,
        field: 'transactionName',
        cellRenderer: GenLegTransNameCell,
    },
    {
        headerName: 'Faktura',
        width: 140,
        field: 'invoiceNo',
        cellRenderer: GenLegInvoiceCell,
    },
    {
        headerName: 'MVA',
        width: 75,
        field: 'vatOptionId',
        cellRenderer: GenLegVatCell,
    },
    {
        headerName: 'Beløp',
        width: 120,
        field: 'amount',
        headerClass: 'header-right',
        cellClass: 'text-right header-right',
        cellClassRules: {
            'font-bold': ({data}: { data: any }) => {
                return ['incoming-balance', 'outgoing-balance', 'movement'].includes(data?.type)
            },
            'text-red-500 line-through': ({data}: { data: any }) => {
                return data?.deleted
            }
        },
        cellRenderer: ({value}: { value: number }) => formatCurrency(value),
        filter: 'agNumberColumnFilter',
        sortable: true
    }
]

</script>

<style>

</style>